import React from 'react'
import { fromJS } from 'immutable'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { getBoundActions } from 'actions'
import { getPrimary } from 'services/languages'
import { ES, FR, DE } from 'services/languages/constants'
import { H1, H2, HEADING_TYPES } from 'components/Heading'
import Link from 'components/Link'
import { URL_TERMS_PRIVACY } from 'services/url/constants'

const dataEn = fromJS({
  introTitle: 'Your Privacy Matters at Gaia!',
  introText: 'At Gaia, Inc., we care about your privacy, and we want to be totally upfront about how we handle your information at Gaia.',
  cookiesTitle: 'COOKIES',
  cookiesText1: 'We use cookies. Unless you adjust your browser settings to refuse cookies, we (and these third parties) will issue cookies when you interact with Gaia. These may be \'session\' cookies, meaning they delete themselves when you leave Gaia, or \'persistent\' cookies which do not delete themselves and help us recognize you when you return so we can provide a tailored service.',
  cookiesText2: 'You can block cookies by activating a setting on your browser allowing you to refuse the setting of cookies. You can also delete cookies through your browser settings. If you use your browser settings to disable, reject, or block cookies (including essential cookies), certain parts of our website or app will not function fully. In some cases, our website or app may not be accessible at all.',
  personalInfoTitle: 'PERSONAL INFORMATION',
  personalInfoText1: 'Gaia only uses and collects sensitive personal information for the purposes allowed by law or with your consent.',
  personalInfoText2: 'We do not sell or provide email addresses to any unauthorized third party and do not authorize any third party or affiliate to misuse products or services created by or associated with Gaia in spam or bulk emails. If you feel you have received any unwanted emails from us, please contact us immediately and we will look into the matter.',
  personalInfoText3: 'If you have a question regarding our privacy practices, please refer to our',
  privacyPolicy: 'Privacy Policy.',
})

const dataEs = fromJS({
  introTitle: '¡Tu privacidad es importante en Gaia!',
  introText: 'En Gaia, Inc., nos preocupamos por tu privacidad y queremos ser totalmente sinceros sobre cómo manejamos tu información en Gaia.',
  cookiesTitle: 'COOKIES',
  cookiesText1: 'Utilizamos cookies, a menos que ajustes la configuración de tu navegador para rechazar las cookies, nosotros (y estos terceros) emitiremos cookies cuando interactúes con Gaia. Estas pueden ser cookies de "sesión", lo que significa que se eliminan a sí mismas cuando abandonas Gaia, o cookies "persistentes" que no se eliminan a sí mismas y nos ayudan a reconocerte cuando regresas para que podamos proporcionarte un servicio personalizado.',
  cookiesText2: 'Puedes bloquear las cookies activando una configuración en tu navegador que te permita rechazar la configuración de cookies. Además, puedes eliminar las cookies a través de la configuración de tu navegador. Si utilizas la configuración de tu navegador para deshabilitar, rechazar o bloquear las cookies (incluidas las cookies esenciales), ciertas partes de nuestro sitio web o aplicación no funcionarán completamente. En algunos casos, es posible que no se pueda acceder a nuestro sitio web o aplicación.',
  personalInfoTitle: 'INFORMACIÓN PERSONAL',
  personalInfoText1: 'Gaia sólo utiliza y recopila información personal sensible para los fines permitidos por la ley o con su consentimiento.',
  personalInfoText2: 'No vendemos ni suministramos direcciones de correos electrónicos a ningún tercero que no esté autorizado y no autorizamos a ningún tercero o afiliado a usar de manera indebida productos o servicios creados por, o asociados con, Gaia en correos electrónicos masivos o spam. Si usted siente que ha recibido de nosotros cualquier correo electrónico no deseado, por favor contáctenos inmediatamente y revisaremos el tema.',
  personalInfoText3: 'Si tiene alguna pregunta sobre nuestras prácticas de privacidad, consulta nuestra',
  privacyPolicy: 'Política de Privacidad.',
})

const dataDe = fromJS({
  introTitle: 'Bei Gaia schätzen wir Ihre Privatsphäre!',
  introText: 'Bei Gaia, Inc, schätzen wir Ihre Privatsphäre und möchten ganz offen damit umgehen, wie wir bei Gaia Ihre Daten nutzen.',
  cookiesTitle: 'COOKIES',
  cookiesText1: 'Wir verwenden Cookies. Wenn Sie Cookies in Ihren Browser-Einstellungen nicht ablehnen, erstellen wir (und diese Drittparteien) Cookies, wenn Sie mit Gaia interagieren. Hierbei kann es sich um Sitzungs-Cookies handeln, die sich beim Verlassen von Gaia automatisch löschen, oder um dauerhafte Cookies, die sich nicht selbst löschen und uns dabei helfen, Sie bei Ihrer Rückkehr zu erkennen und Ihnen einen maßgeschneiderten Dienst anzubieten.',
  cookiesText2: 'Sie können Cookies blockieren, indem Sie eine Einstellung in Ihrem Browser aktivieren, mit der Sie die Erstellung von Cookies ablehnen. Sie können Cookies auch über Ihre Browser-Einstellungen löschen. Wenn Sie Cookies über Ihre Browser-Einstellungen deaktivieren, ablehnen oder blocken (einschließlich erforderlicher Cookies), funktionieren bestimmte Teile unserer Webseite nicht vollständig. In bestimmten Fällen ist kein Zugriff auf unsere Webseite oder App möglich.',
  personalInfoTitle: 'PERSONENBEZOGENE DATEN',
  personalInfoText1: 'Gaia verwendet und sammelt sensible personenbezogene Daten nur für die gesetzlich zulässigen Zwecke oder mit Ihrer Zustimmung.',
  personalInfoText2: 'Wir verkaufen oder stellen keine email Adressen an unberechtigten Drittparteien bereit und berechtigen keine Drittpartei oder Tochterfirma um Produkte oder Dienste zu missbrauchen, die erstellt von oder assoziiert mit Gaia in spam oder Massenemails. Wenn Sie der Ansicht sind, dass Sie von uns ungewollte emails von uns erhielten, kontaktieren sie uns unverzüglich und wir werden uns die Sache ansehen.',
  personalInfoText3: 'Wenn Sie Fragen zu unseren Datenschutzpraktiken haben, lesen Sie bitte unsere',
  privacyPolicy: 'Datenschutzbestimmungen.',
})

const dataFr = fromJS({
  introTitle: 'Respect de la vie privée chez Gaia',
  introText: 'Chez Gaia, Inc., nous attachons une grande importance à votre vie privée et nous souhaitons être totalement transparents quant à la manière dont nous traitons vos informations.',
  cookiesTitle: 'COOKIES',
  cookiesText1: 'Nous utilisons des cookies. À moins que vous ajustiez les paramètres de votre navigateur pour refuser les cookies, nous (et ces tiers) émettrons des cookies lorsque vous interagissez avec Gaia. Il peut s\'agir de cookies « de session », c\'est-à-dire qu\'ils se suppriment automatiquement lorsque vous quittez Gaia, ou de cookies « persistants » qui ne se suppriment pas et nous aident à vous reconnaître lorsque vous revenez afin que nous puissions vous fournir un service personnalisé.',
  cookiesText2: 'Vous pouvez bloquer les cookies en activant un paramètre sur votre navigateur vous permettant de refuser le dépôt de cookies. Vous pouvez également supprimer les cookies via les paramètres de votre navigateur. Si vous utilisez les paramètres de votre navigateur pour désactiver, rejeter ou bloquer les cookies (y compris les cookies essentiels), certaines parties de notre site Web ou de notre application ne fonctionneront pas pleinement. Dans certains cas, notre site Web ou notre application peut ne pas être accessible du tout.',
  personalInfoTitle: 'INFORMATIONS PERSONNELLES',
  personalInfoText1: 'Gaia utilise et collecte des informations personnelles sensibles uniquement conformément à la loi ou avec votre consentement.',
  personalInfoText2: 'Nous ne vendons ni ne fournissons des adresses de courrier électronique à un tiers non autorisé et nous n\'autorisons aucun tiers ou affilié à utiliser de manière abusive des produits ou des services créés ou associés à Gaia dans des courriers indésirables ou spam. Si vous pensez avoir reçu des courriers électroniques indésirables, contactez-nous immédiatement et nous examinerons la question.',
  personalInfoText3: 'Si vous avez des questions  concernant nos pratiques de confidentialité, veuillez consulter notre',
  privacyPolicy: 'Politique de Confidentialité.',
})

function getData (language) {
  switch (language) {
    case ES:
      return dataEs
    case DE:
      return dataDe
    case FR:
      return dataFr
    default:
      return dataEn
  }
}

function ModalContent (props) {
  const {
    text,
    dismissModal,
  } = props

  const onClickPrivacy = () => {
    dismissModal()
  }

  return (
    <div className="modal-cookie-compliance__content">
      <H1
        className="modal-cookie-compliance__title modal-cookie-compliance__title--first"
        as={HEADING_TYPES.H6}
      >
        {text.get('introTitle')}
      </H1>
      <p className="modal-cookie-compliance__paragraph">
        {text.get('introText')}
      </p>
      <H2
        className="modal-cookie-compliance__title"
        as={HEADING_TYPES.H6}
      >
        {text.get('cookiesTitle')}
      </H2>
      <p className="modal-cookie-compliance__paragraph">
        {text.get('cookiesText1')}
      </p>
      <p className="modal-cookie-compliance__paragraph">
        {text.get('cookiesText2')}
      </p>
      <H2
        className="modal-cookie-compliance__title"
        as={HEADING_TYPES.H6}
      >
        {text.get('personalInfoTitle')}
      </H2>
      <p className="modal-cookie-compliance__paragraph">
        {text.get('personalInfoText1')}
      </p>
      <p className="modal-cookie-compliance__paragraph">
        {text.get('personalInfoText2')}
      </p>
      <p className="modal-cookie-compliance__paragraph">
        {`${text.get('personalInfoText3')} `}
        <Link
          to={`${URL_TERMS_PRIVACY}#privacy-policy`}
          onClick={onClickPrivacy}
        >
          {text.get('privacyPolicy')}
        </Link>
      </p>
    </div>
  )
}

function ModalCookieCompliance (props) {
  const { userLanguage } = props
  const text = getData(userLanguage)

  return (
    <div className="modal-cookie-compliance">
      <ModalContent {...props} text={text} />
    </div>
  )
}

ModalCookieCompliance.propTypes = {
  userLanguage: PropTypes.string,
  dismissModal: PropTypes.func.isRequired,
}

export default reduxConnect(
  (state) => ({
    userLanguage: getPrimary(state.user.getIn(['data', 'language'])),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      dismissModal: actions.dialog.dismissModal,
    }
  },
)(ModalCookieCompliance)
